import type React from 'react'
import { useEffect } from 'react'

import type { Api_Video } from '@core/api'
import { isLiveStreamIdle, isLiveStreamLive } from '@core/livestream/helpers'

import {
  buildBroadcastEventSchema,
  buildEventSchema,
  buildVideoObjectSchema,
} from './schemas'

interface VideoFeedJsonLdProps {
  feedVideos: Api_Video[]
}

function buildSchemaForVideo(video: Api_Video): object {
  if (isLiveStreamLive(video)) {
    return buildBroadcastEventSchema(video)
  }
  if (isLiveStreamIdle(video)) {
    return buildEventSchema(video)
  }
  return buildVideoObjectSchema(video)
}

/**
 * Generates JSON-LD structured data for all videos in feedVideos and injects
 * them into the document head. Emits BroadcastEvent for live streams,
 * Event for upcoming sessions, and VideoObject for replays and regular videos.
 */
export const VideoFeedJsonLd: React.FC<VideoFeedJsonLdProps> = ({
  feedVideos,
}) => {
  useEffect(() => {
    if (!feedVideos?.length) {
      return
    }

    const scripts = feedVideos.map((video) => {
      const schema = buildSchemaForVideo(video)
      const script = document.createElement('script')
      script.type = 'application/ld+json'
      script.textContent = JSON.stringify(schema)
      document.head.appendChild(script)
      return script
    })

    return () => {
      scripts.forEach((script) => script.remove())
    }
  }, [feedVideos])

  return null
}
